import { useState } from "react";
import API from "../config/api.js";

const REASONS = [
  { value: "not_getting_enough_inquiries", label: "I'm not getting enough inquiries yet" },
  { value: "not_using_consistently",       label: "I'm not using Social Jet consistently" },
  { value: "not_in_budget_right_now",      label: "It's not in my budget right now" },
  { value: "technical_issue",              label: "I had a technical issue" },
  { value: "found_another_solution",       label: "I found another solution" },
  { value: "other",                        label: "Other" },
];

export default function AccountSubscriptionsCancelationModal({ idsubscription, gateway, idgateway, onClose }) {
  const [step, setStep]               = useState(1);
  const [reason, setReason]           = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [confirmed, setConfirmed]     = useState(false);
  const [cancelling, setCancelling]   = useState(false);

  const subData = { idsubscription, gateway, idgateway };

  const close = () => onClose?.();

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const endpoint = subData.gateway === "stripe" ? API.stripe : API.paypal;
      const fd = new FormData();
      fd.append("idgateway",        subData.idgateway);
      fd.append("idsubscription",   subData.idsubscription);
      fd.append("cancelationreason", reason === "other" ? otherReason : reason);
      const res = await fetch(`${endpoint}?cancelSubscription`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!res.ok) throw new Error("cancelSubscription failed");
      close();
      window.location.reload();
    } catch (err) {
      console.error("cancelSubscription error:", err);
      alert("Error: cancelSubscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleApplyOffer = async () => {
    try {
      const fd = new FormData();
      fd.append("idgateway", subData.idgateway);
      await fetch(`${API.stripe}?applyOfferForNonCancelation`, {
        method: "POST", credentials: "include", body: fd,
      });
    } catch (err) {
      console.error("applyOffer error:", err);
    }
    setStep("welcome-back");
  };

  if (!step) return null;

  const Backdrop = ({ onClick }) => (
    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={onClick} />
  );

  const ModalShell = ({ size = "", children }) => (
    <div className={`modal fade show d-block`} style={{ zIndex: 1050 }} role="dialog">
      <div className={`modal-dialog modal-dialog-centered ${size}`}>
        <div className="modal-content">{children}</div>
      </div>
    </div>
  );

  // ── Step 1 — Select reason ────────────────────────────────────────────────
  if (step === 1) return (
    <>
      <Backdrop onClick={close} />
      <ModalShell>
        <div className="modal-header">
          <h5 className="modal-title">Pause your inquiry engine and miss out on inquiries?</h5>
          <button type="button" className="btn-close" onClick={close} />
        </div>
        <div className="modal-body">
          <p>Before you leave, we'd like to understand why you're canceling your subscription. This will help us improve our services.</p>
          <h6>Please select a reason for cancelling:</h6>
          {REASONS.map((r) => (
            <div key={r.value} className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="reason"
                id={`reason-${r.value}`}
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
              />
              <label className="form-check-label text-muted" htmlFor={`reason-${r.value}`}>
                {r.label}
              </label>
              {r.value === "other" && reason === "other" && (
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder="Please type here..."
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-primary" onClick={close}>Go back</button>
          <button type="button" className="btn btn-primary" disabled={!reason} onClick={() => setStep(2)}>
            Continue
          </button>
        </div>
      </ModalShell>
    </>
  );

  // ── Step 2 — Retention offer ──────────────────────────────────────────────
  if (step === 2) return (
    <>
      <Backdrop onClick={close} />
      <ModalShell>
        <div className="modal-header">
          <h5 className="modal-title">Before you shut off your inquiry engine…</h5>
          <button type="button" className="btn-close" onClick={close} />
        </div>
        <div className="modal-body">
          <p><strong>Travel Agent Collective</strong> is built to generate consistent, higher-quality client conversations without you overthinking what to post.</p>
          <p>If momentum has slowed, let's fix that.</p>
          <p>
            <strong>Stay and you'll receive:</strong><br />
            • 20% off your next 3 months<br />
            • A complimentary 1:1 success call to sharpen your inquiry strategy
          </p>
          <p>Sometimes one small adjustment changes everything.</p>
          <p><strong>Your 20% savings will be automatically applied for the next 3 billing cycles.</strong></p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-primary" onClick={handleApplyOffer}>
            Keep My Inquiry Engine Running
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setStep(3)}>
            Continue To Cancel
          </button>
        </div>
      </ModalShell>
    </>
  );

  // ── Step 3 — Final confirmation ───────────────────────────────────────────
  if (step === 3) return (
    <>
      <Backdrop onClick={close} />
      <ModalShell size="modal-lg">
        <div className="modal-header">
          <h5 className="modal-title">Are you sure you want to turn off your inquiry engine and miss out on conversations with new clients?</h5>
          <button type="button" className="btn-close" onClick={close} />
        </div>
        <div className="modal-body">
          <h6>Here's what stops when your membership ends:</h6>
          {[
            "A system designed to get you weekly inquiries",
            "Pre-built content designed to attract higher-quality clients",
            "Done-for-you messaging that reduces ghosting",
            "The 30-Day Success Path + quarterly revenue accelerators",
            "Consistent visibility without scrambling for ideas",
            "Proven messaging designed to attract better-fit clients",
            "Quarterly revenue accelerators to sharpen your strategy",
            "Done-for-you content that keeps inquiries flowing",
          ].map((item) => (
            <p key={item} className="ms-1"><span className="me-2">✔</span>{item}</p>
          ))}
          <h6>One new booking typically covers your entire membership for the year.</h6>
          <p>Need help or have questions? <a href="#chat" className="link-primary">Start Chat Support</a></p>
        </div>
        <div className="modal-footer d-block">
          <div className="row g-0 align-items-center">
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="confirm-cancel-check"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <label className="form-check-label text-muted" htmlFor="confirm-cancel-check">
                  I understand that by canceling my subscription, I will lose all paid features at the end of my billing cycle.
                </label>
              </div>
            </div>
            <div className="col-md-6 text-end">
              <button type="button" className="btn btn-outline-primary me-4" onClick={close}>
                Back To My Account
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!confirmed || cancelling}
                onClick={handleCancel}
              >
                {cancelling ? "Cancelling..." : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        </div>
      </ModalShell>
    </>
  );

  // ── Welcome back ──────────────────────────────────────────────────────────
  if (step === "welcome-back") return (
    <>
      <Backdrop onClick={close} />
      <ModalShell>
        <div className="modal-header">
          <h5 className="modal-title">Welcome Back! 🎉🥂</h5>
          <button type="button" className="btn-close" onClick={close} />
        </div>
        <div className="modal-body">
          <p>Thank you for choosing to stay with us! Your 20% discount has been applied to your account. Remember, you can schedule your personalized onboarding call anytime through chat support.</p>
          <p>We're here to help you grow your business. Let's do this! 🙌</p>
        </div>
        <div className="modal-footer d-block">
          <a href="/launchpad" className="btn btn-outline-primary me-4">Return to Dashboard</a>
        </div>
      </ModalShell>
    </>
  );

  return null;
}